/**
 * Microsoft Graph list/load/save for SharePoint-backed documents.
 * Okta access token is exchanged for a Graph token via POST /auth/graph-token (no MSAL in add-in).
 * // TODO: confirm /auth/graph-token path with backend team if renamed.
 */

import { getDefaultGraphDriveId, GRAPH_API_BASE, getGraphSiteId } from "../config/graphConfig";
import { apiRequest } from "./apiClient";
import { openDraftInHost } from "./hostDocumentService";
import type { Template } from "../types/template";
import { formatDisplayDate, formatDisplayDateTime } from "../utils/displayDate";

const TOKEN_SKEW_MS = 60_000;

type GraphModuleState = {
  azureAccessToken: string | null;
  azureTokenExpiresAtMs: number | null;
  currentDriveId: string | null;
  currentItemId: string | null;
  lastListSiteId: string | null;
};

const graphState: GraphModuleState = {
  azureAccessToken: null,
  azureTokenExpiresAtMs: null,
  currentDriveId: null,
  currentItemId: null,
  lastListSiteId: null
};

type GraphTokenResponse = {
  readonly success: boolean;
  readonly data: {
    readonly access_token: string;
    readonly expires_in: number;
    readonly token_type: string;
  };
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** @internal exported for tests */
export function _resetGraphServiceStateForTests(): void {
  graphState.azureAccessToken = null;
  graphState.azureTokenExpiresAtMs = null;
  graphState.currentDriveId = null;
  graphState.currentItemId = null;
  graphState.lastListSiteId = null;
}

export function clearActiveSharePointItem(): void {
  graphState.currentDriveId = null;
  graphState.currentItemId = null;
}

/**
 * Remember drive + item for Graph `PUT …/content` when the document was not opened via {@link loadFileIntoWord}
 * but the API exposes the same targets on `stored_file` (e.g. after v2 commit / SharePoint-backed rows).
 */
export function setActiveSharePointBinding(driveId: string, itemId: string): void {
  const d = driveId.trim();
  const i = itemId.trim();
  if (!d || !i) {
    return;
  }
  graphState.currentDriveId = d;
  graphState.currentItemId = i;
}

export function hasActiveSharePointItem(): boolean {
  return Boolean(graphState.currentDriveId && graphState.currentItemId);
}

export function getActiveSharePointItem(): { driveId: string; itemId: string } | null {
  const { currentDriveId, currentItemId } = graphState;
  if (currentDriveId && currentItemId) {
    return { driveId: currentDriveId, itemId: currentItemId };
  }
  return null;
}

async function getGraphAccessToken(): Promise<string> {
  const now = Date.now();
  if (
    graphState.azureAccessToken &&
    graphState.azureTokenExpiresAtMs != null &&
    now < graphState.azureTokenExpiresAtMs - TOKEN_SKEW_MS
  ) {
    return graphState.azureAccessToken;
  }

  const response = await apiRequest(
    "/auth/graph-token",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}"
    },
    { requiresAuth: true, redirectOnUnauthorized: true }
  );

  const json = (await response.json()) as GraphTokenResponse;
  const token = json.data?.access_token?.trim();
  const expiresIn = json.data?.expires_in;
  if (!token || typeof expiresIn !== "number") {
    throw new Error("Graph token exchange returned an invalid payload.");
  }

  graphState.azureAccessToken = token;
  graphState.azureTokenExpiresAtMs = now + expiresIn * 1000;
  return token;
}

/** Clear cached Azure Graph token (e.g. after Graph returns 401 on upload). */
export function clearGraphAccessTokenCache(): void {
  graphState.azureAccessToken = null;
  graphState.azureTokenExpiresAtMs = null;
}

export type GraphDriveListItem = {
  readonly id: string;
  readonly name: string;
  readonly driveId: string;
  readonly lastModifiedDateTime: string | null;
  readonly isFile: boolean;
};

function parseDriveListPayload(raw: unknown): GraphDriveListItem[] {
  if (!raw || typeof raw !== "object") return [];
  const value = (raw as { value?: unknown }).value;
  if (!Array.isArray(value)) return [];

  const out: GraphDriveListItem[] = [];
  for (const row of value) {
    if (!row || typeof row !== "object") continue;
    const o = row as Record<string, unknown>;
    const id = typeof o.id === "string" ? o.id : null;
    const name = typeof o.name === "string" ? o.name : "";
    const parent = o.parentReference;
    const driveId =
      parent && typeof parent === "object" && typeof (parent as { driveId?: string }).driveId === "string"
        ? (parent as { driveId: string }).driveId
        : null;
    const lm = o.lastModifiedDateTime;
    const lastModifiedDateTime = typeof lm === "string" ? lm : null;
    const isFolder = typeof o.folder === "object" && o.folder !== null;
    if (!id || !driveId) continue;
    out.push({
      id,
      name: name || "Untitled",
      driveId,
      lastModifiedDateTime,
      isFile: !isFolder
    });
  }
  return out;
}

/**
 * List drive root children for a site. Requires Files.Read / Sites.Read on the Graph app.
 */
export async function listFiles(siteId: string): Promise<GraphDriveListItem[]> {
  graphState.lastListSiteId = siteId;
  const token = await getGraphAccessToken();
  const url = `${GRAPH_API_BASE}/sites/${encodeURIComponent(siteId)}/drive/root/children`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Graph list failed (${res.status}): ${text.slice(0, 400)}`);
  }
  const json: unknown = await res.json();
  return parseDriveListPayload(json);
}

/**
 * Map Graph items to Template rows for Template Library (data-only; optional feature flag).
 */
export function mapGraphItemsToTemplates(items: GraphDriveListItem[]): Template[] {
  return items
    .filter((it) => it.isFile && it.name.toLowerCase().endsWith(".docx"))
    .map((it) => ({
      id: it.id,
      name: it.name,
      templateType: "SharePoint",
      businessUnits: [],
      createdByName: null,
      createdAt: it.lastModifiedDateTime
    }));
}

/**
 * Same as mapGraphItemsToTemplates but applies constitution date formatting for display columns that expect preformatted strings.
 */
export function mapGraphItemsToTemplatesWithDisplayDates(items: GraphDriveListItem[]): Template[] {
  return items
    .filter((it) => it.isFile && it.name.toLowerCase().endsWith(".docx"))
    .map((it) => ({
      id: it.id,
      name: it.name,
      templateType: "SharePoint",
      businessUnits: [],
      createdByName: null,
      createdAt: it.lastModifiedDateTime,
      graphDriveId: it.driveId
    }));
}

/** Load remote item bytes into the current Word document and remember drive/item for save. */
export async function loadFileIntoWord(driveId: string, itemId: string): Promise<void> {
  const token = await getGraphAccessToken();
  const url = `${GRAPH_API_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Graph download failed (${res.status}): ${text.slice(0, 400)}`);
  }
  const buf = await res.arrayBuffer();
  const base64 = arrayBufferToBase64(buf);
  await openDraftInHost(base64);
  graphState.currentDriveId = driveId;
  graphState.currentItemId = itemId;
}

async function ensureOfficeDocumentHost(): Promise<void> {
  if (typeof Office === "undefined") {
    throw new Error("Office.js is not loaded");
  }
  await Office.onReady();
  if (!Office.context?.document?.getFileAsync) {
    throw new Error("Host document file API is not available in this Word instance");
  }
}

function getHostDocumentCompressedBytes(): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4 * 1024 * 1024 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded || !result.value) {
          reject(
            new Error(
              result.error?.message ?? "getFileAsync failed"
            )
          );
          return;
        }
        const file = result.value;
        const sliceCount = file.sliceCount;
        const parts: Uint8Array[] = [];

        const fail = (msg: string) => {
          file.closeAsync(() => {
            reject(new Error(msg));
          });
        };

        const readSlice = (index: number) => {
          if (index >= sliceCount) {
            const total = parts.reduce((n, p) => n + p.length, 0);
            const merged = new Uint8Array(total);
            let offset = 0;
            for (const p of parts) {
              merged.set(p, offset);
              offset += p.length;
            }
            file.closeAsync(() => resolve(merged));
            return;
          }
          file.getSliceAsync(index, (sliceResult) => {
            if (sliceResult.status !== Office.AsyncResultStatus.Succeeded || !sliceResult.value) {
              fail(sliceResult.error?.message ?? "getSliceAsync failed");
              return;
            }
            parts.push(new Uint8Array(sliceResult.value.data));
            readSlice(index + 1);
          });
        };

        readSlice(0);
      }
    );
  });
}

/** Upload current Word document bytes to the given drive item (overwrite). */
export async function saveFileToSharePoint(driveId: string, itemId: string): Promise<void> {
  await ensureOfficeDocumentHost();
  const token = await getGraphAccessToken();
  const bytes = await getHostDocumentCompressedBytes();
  const url = `${GRAPH_API_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  const bodyCopy = Uint8Array.from(bytes);
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    },
    body: new Blob([bodyCopy], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    })
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Graph upload failed (${res.status}): ${text.slice(0, 400)}`);
  }
}

/**
 * Save using module state from the last successful `loadFileIntoWord`.
 */
export async function saveActiveFileToSharePoint(): Promise<void> {
  const active = getActiveSharePointItem();
  if (!active) {
    throw new Error("No SharePoint file is loaded in this session.");
  }
  await saveFileToSharePoint(active.driveId, active.itemId);
}

const WORD_DOCX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Session key for post-commit Graph upload status (read on Send for Review page). */
export const POST_COMMIT_GRAPH_MSG_KEY = "contractiq_graph_post_commit_msg";

/** FR-005b normative English copy for post-commit upload (v1). */
export const POST_COMMIT_GRAPH_MESSAGES = {
  saving: "Saving document to SharePoint…",
  success: "Document saved to SharePoint successfully",
  failure: "Contract committed but SharePoint save failed — please save manually"
} as const;

function notifyPostCommitGraphBanner(message: string): void {
  try {
    sessionStorage.setItem(POST_COMMIT_GRAPH_MSG_KEY, message);
  } catch {
    /* ignore */
  }
}

async function putHostDocumentToGraphContent(
  driveId: string,
  itemId: string,
  accessToken: string,
  bytes: Uint8Array
): Promise<Response> {
  const url = `${GRAPH_API_BASE}/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  const bodyCopy = Uint8Array.from(bytes);
  return fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": WORD_DOCX_CONTENT_TYPE
    },
    body: new Blob([bodyCopy], { type: WORD_DOCX_CONTENT_TYPE })
  });
}

/**
 * After successful v2 commit: upload current Word bytes to Graph (**FR-005a**), with **FR-005b** banner strings
 * and **FR-005c** (401 → clear token, re-exchange, single PUT retry). Does not throw on upload failure.
 */
export async function runPostCommitSharePointUpload(options: {
  graphDriveId: string | null | undefined;
  graphItemId: string | null | undefined;
}): Promise<void> {
  const itemId = (options.graphItemId ?? "").trim();
  if (!itemId) {
    return;
  }
  const driveId =
    (options.graphDriveId ?? "").trim() || getDefaultGraphDriveId()?.trim() || "";
  if (!driveId) {
    notifyPostCommitGraphBanner(POST_COMMIT_GRAPH_MESSAGES.failure);
    return;
  }

  await ensureOfficeDocumentHost();
  notifyPostCommitGraphBanner(POST_COMMIT_GRAPH_MESSAGES.saving);

  let bytes: Uint8Array;
  try {
    bytes = await getHostDocumentCompressedBytes();
  } catch {
    notifyPostCommitGraphBanner(POST_COMMIT_GRAPH_MESSAGES.failure);
    return;
  }

  let token = await getGraphAccessToken();
  let res = await putHostDocumentToGraphContent(driveId, itemId, token, bytes);

  if (res.status === 401) {
    clearGraphAccessTokenCache();
    try {
      token = await getGraphAccessToken();
    } catch {
      notifyPostCommitGraphBanner(POST_COMMIT_GRAPH_MESSAGES.failure);
      return;
    }
    res = await putHostDocumentToGraphContent(driveId, itemId, token, bytes);
  }

  if (res.ok) {
    notifyPostCommitGraphBanner(POST_COMMIT_GRAPH_MESSAGES.success);
  } else {
    notifyPostCommitGraphBanner(POST_COMMIT_GRAPH_MESSAGES.failure);
  }
}

/**
 * List `.docx` files when `VITE_GRAPH_SITE_ID` is set (used by Template Library opt-in).
 */
export async function listGraphTemplatesForLibrary(): Promise<Template[]> {
  const siteId = getGraphSiteId();
  if (!siteId) {
    throw new Error("Graph site id is not configured (VITE_GRAPH_SITE_ID).");
  }
  const items = await listFiles(siteId);
  return mapGraphItemsToTemplatesWithDisplayDates(items);
}

/** Constitution-compliant display string for Graph `lastModifiedDateTime` in list surfaces. */
export function formatGraphFileTimestamp(iso: string | null | undefined): string {
  if (iso == null || iso === "") return "—";
  return /T\d{2}:\d{2}/.test(iso) ? formatDisplayDateTime(iso) : formatDisplayDate(iso);
}
